import { ActionModel } from '@klant/conversational-connector/klant-conversational-connector.model';
import {
    ChatMessageModel,
    ChoicePromptMessageModel,
    CTAMessageModel,
    MessageStatusModel,
    ParticipantMessageModel,
    PlainTextMessageModel
} from '@klant/conversational-connector/models/ChatMessage.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';
import { MessageCtaComponent } from '@klant/conversational/conversational-chat-message/message-cta/message-cta.component';

import { Component, ComponentRef, input, OnInit, output, ViewChild } from '@angular/core';

import { ConversationalChatMessageDirective } from './conversational-chat-message.directive';
import { MessageChoicePromptComponent } from './message-choice-prompt/message-choice-prompt.component';
import { MessageMessageComponent } from './message-message/message-message.component';
import { MessageDividerComponent } from './message-divider/message-divider.component';

@Component({
    selector: 'conversational-chat-message',
    templateUrl: './conversational-chat-message.component.html',
    standalone: false
})
export class ConversationalChatMessageComponent implements OnInit {
    public chatMessage = input<ChatMessageModel>(null);
    public isLastMessage = input<boolean>(false);
    public showAvatar = input<boolean>(false);

    public sendAction = output<ActionModel>();

    @ViewChild(ConversationalChatMessageDirective, { static: true })
    public conversationalChatMessage: ConversationalChatMessageDirective;

    public ngOnInit(): void {
        const chatMessage: ChatMessageModel = this.chatMessage();

        switch (chatMessage.type) {
            case TypeModel.PLAIN_TEXT_MESSAGE:
                this.createPlainTextMessage(chatMessage as PlainTextMessageModel);
                break;
            case TypeModel.CHOICE_PROMPT_MESSAGE:
                this.createMessageChoicePrompt(chatMessage as ChoicePromptMessageModel);
                break;
            case TypeModel.PARTICIPANT_MESSAGE:
                this.createParticipantMessage(chatMessage as ParticipantMessageModel);
                break;
            case TypeModel.CTA_MESSAGE:
                this.createMessageCTA(chatMessage as CTAMessageModel);
                break;
        }
    }

    private createPlainTextMessage(plainTextMessage: PlainTextMessageModel): void {
        const componentRef = this.createComponentRef<MessageMessageComponent>(MessageMessageComponent);
        componentRef.instance.text = plainTextMessage.text;
        componentRef.instance.fromClient.set(plainTextMessage.fromClient);
        componentRef.instance.showAvatar.set(this.showAvatar());
        componentRef.instance.agentOrBot.set(plainTextMessage.serviceName);
        componentRef.instance.occurredAt.set(plainTextMessage.occurredAt);
        componentRef.instance.isMessagePending.set(this.isMessageStatusPending(plainTextMessage.messageStatus));
    }

    private createMessageChoicePrompt(choicePromptMessage: ChoicePromptMessageModel): void {
        const componentRef = this.createComponentRef<MessageChoicePromptComponent>(MessageChoicePromptComponent);
        componentRef.instance.messageChoicePrompt.set(choicePromptMessage);
        componentRef.instance.isLastMessage = this.isLastMessage;
        componentRef.instance.occurredAt.set(choicePromptMessage.occurredAt);
        componentRef.instance.showAvatar.set(this.showAvatar());
        componentRef.instance.sendAction.subscribe((action: ActionModel) => {
            this.sendAction.emit(action);
        });
    }

    private createMessageCTA(cta: CTAMessageModel): void {
        const componentRef = this.createComponentRef<MessageCtaComponent>(MessageCtaComponent);
        componentRef.instance.buttonLink = cta.buttonLink;
        componentRef.instance.buttonText = cta.buttonText;
    }

    private createParticipantMessage(participantMessageModel: ParticipantMessageModel): void {
        const introductionText = participantMessageModel.introductionText;
        if (introductionText) {
            const componentRef = this.createComponentRef<MessageDividerComponent>(MessageDividerComponent);
            componentRef.instance.text.set(introductionText.replace('{{name}}', participantMessageModel.name));
        }
    }

    private createComponentRef<Component>(component): ComponentRef<Component> {
        const viewContainerRef = this.conversationalChatMessage.viewContainerRef;
        viewContainerRef.clear();
        return viewContainerRef.createComponent(component);
    }

    private isMessageStatusPending(messageStatus: MessageStatusModel): boolean {
        return !(messageStatus === MessageStatusModel.RECEIVED || messageStatus === MessageStatusModel.READ);
    }
}
